const blocksApiUrl = import.meta.env.VITE_BLOCKS_API_URL || 'https://api.seliseblocks.com';

const OIDC_CONFIG = {
  issuer:
    import.meta.env.VITE_OIDC_API_URL ||
    import.meta.env.VITE_API_URL ||
    `${blocksApiUrl.replace(/\/$/, '')}/iam/v4`,
  iamUrl:
    import.meta.env.VITE_OIDC_IAM_URL ||
    import.meta.env.VITE_IAM_URL ||
    'https://iam.seliseblocks.com',
  tenantId:
    import.meta.env.VITE_OIDC_TENANT_ID ||
    import.meta.env.VITE_TENANT_ID ||
    import.meta.env.VITE_X_BLOCKS_KEY ||
    '',
  clientId: import.meta.env.VITE_OIDC_CLIENT_ID || import.meta.env.VITE_CLIENT_ID || '',
  clientSecret:
    import.meta.env.VITE_OIDC_CLIENT_SECRET || import.meta.env.VITE_CLIENT_SECRET || '',
  redirectUri:
    import.meta.env.VITE_OIDC_REDIRECT_URI ||
    import.meta.env.VITE_REDIRECT_URI ||
    `${window.location.origin}/callback`,
  scope: import.meta.env.VITE_OIDC_SCOPE || 'openid profile email',
};

const localAuthKeys = [
  'access_token',
  'accessToken',
  'authToken',
  'token',
  'blocks-os-token',
  'id_token',
  'refresh_token',
  'token_expiry',
  'auth_method',
  'actor',
];

const sessionAuthKeys = ['oidc_state', 'oidc_nonce', 'oidc_code_verifier'];

/**
 * Hosted OIDC (cookie) login needs ONLY the project context (x-blocks-key). Per the
 * IAM swagger, `clientId` and `redirectUri` on GET /idp/initiate are optional — the
 * platform resolves the project's default OIDC client and its redirect URI when they
 * are omitted. No client secret is involved: the platform performs the token exchange
 * server-side and sets an HTTP-only cookie, so the browser never hits the token endpoint.
 */
function assertHostedLoginConfig() {
  if (!OIDC_CONFIG.tenantId) {
    throw new Error('Missing OIDC configuration: VITE_OIDC_TENANT_ID or VITE_X_BLOCKS_KEY');
  }
}

/** Embedded login only needs the project context (x-blocks-key); no OIDC client. */
function assertLoginConfig() {
  if (!OIDC_CONFIG.tenantId) {
    throw new Error('Missing auth configuration: VITE_OIDC_TENANT_ID or VITE_X_BLOCKS_KEY');
  }
}

/** Pull a human-readable message out of a Blocks error envelope, if present. */
function extractAuthError(payload) {
  if (!payload || typeof payload !== 'object') return '';
  if (typeof payload.message === 'string' && payload.message.trim()) return payload.message.trim();
  if (typeof payload.error_description === 'string') return payload.error_description;
  if (typeof payload.error === 'string') return payload.error;
  const errors = payload.errors || payload.data?.errors;
  if (errors && typeof errors === 'object') {
    const first = Object.values(errors).flat().filter(Boolean)[0];
    if (typeof first === 'string') return first;
  }
  return '';
}

function normalizeAuthUrl(rawUrl) {
  try {
    const url = new URL(rawUrl);
    const issuerUrl = new URL(OIDC_CONFIG.issuer);
    const issuerBasePath = issuerUrl.pathname.replace(/\/$/, '');
    if (url.origin === issuerUrl.origin) {
      let pathname = url.pathname;
      if (pathname.startsWith(`${issuerBasePath}/`) || pathname === issuerBasePath) {
        pathname = pathname.slice(issuerBasePath.length) || '/';
      }
      return `${OIDC_CONFIG.iamUrl}${pathname}${url.search}${url.hash}`;
    }
    return rawUrl;
  } catch {
    return rawUrl;
  }
}

function readTokenValue(payload, keys) {
  for (const key of keys) {
    if (typeof payload?.[key] === 'string' && payload[key].trim()) return payload[key].trim();
  }
  return '';
}

function normalizeTokenResponse(payload) {
  const tokenPayload = payload?.data && typeof payload.data === 'object' ? payload.data : payload;
  return {
    ...tokenPayload,
    access_token: readTokenValue(tokenPayload, ['access_token', 'accessToken', 'AccessToken']),
    id_token: readTokenValue(tokenPayload, ['id_token', 'idToken', 'IdToken']),
    refresh_token: readTokenValue(tokenPayload, ['refresh_token', 'refreshToken', 'RefreshToken']),
    expires_in:
      tokenPayload?.expires_in ||
      tokenPayload?.expiresIn ||
      tokenPayload?.ExpiresIn ||
      tokenPayload?.expiration ||
      tokenPayload?.Expiration,
  };
}

function clearAuthStorage() {
  localAuthKeys.forEach((key) => localStorage.removeItem(key));
  sessionAuthKeys.forEach((key) => sessionStorage.removeItem(key));
}

/** Decode a JWT payload without verifying the signature. Returns null on failure. */
function decodeJwtPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(
      decodeURIComponent(
        atob(padded).split('').map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`).join(''),
      ),
    );
  } catch {
    return null;
  }
}

/**
 * Compute the absolute token-expiry timestamp (ms since epoch).
 *
 * ⚠️ Blocks IAM returns `expires_in` in MINUTES, not seconds (e.g. 15 = 15 min,
 * verified by decoding the JWT: exp − iat = 900s). Prefer the JWT `exp` claim,
 * then `expires_utc`, and only fall back to `expires_in` treated as minutes.
 */
function computeTokenExpiry(tokens) {
  const claims = tokens.access_token ? decodeJwtPayload(tokens.access_token) : null;
  if (claims?.exp) return claims.exp * 1000;

  const utc = tokens.expires_utc || tokens.expiresUtc || tokens.ExpiresUtc;
  const parsedUtc = utc ? Date.parse(utc) : NaN;
  if (!Number.isNaN(parsedUtc)) return parsedUtc;

  if (tokens.expires_in) return Date.now() + Number(tokens.expires_in) * 60 * 1000;
  return 0;
}

/**
 * Persist a normalized token payload to localStorage. Records `authMethod`
 * ('embedded' | 'oidc') so refresh/logout can target the right endpoints —
 * embedded tokens (`/auth/login`) refresh via `/auth/refresh` and revoke via
 * `/auth/logout`, whereas OIDC tokens use `/oidc/token` and `/oidc/revoke`.
 */
function persistTokens(tokens, authMethod) {
  if (tokens.access_token) localStorage.setItem('access_token', tokens.access_token);
  else localStorage.removeItem('access_token');
  if (tokens.id_token) localStorage.setItem('id_token', tokens.id_token);
  if (tokens.refresh_token) localStorage.setItem('refresh_token', tokens.refresh_token);
  if (authMethod) localStorage.setItem('auth_method', authMethod);

  const expiry = computeTokenExpiry(tokens);
  if (expiry) localStorage.setItem('token_expiry', String(expiry));
  else localStorage.removeItem('token_expiry');
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Start the Blocks-hosted OIDC login (cookie flow). Fetches `GET /idp/initiate`,
 * then navigates the browser to the hosted authorization URL it returns. Blocks
 * runs the authorization-code (PKCE) flow itself and, on return, its `/idp/callback`
 * exchanges the code and sets a secure HTTP-only session cookie — the SPA never
 * handles a code, a client secret, or raw tokens. Requires tenantId + clientId +
 * redirectUri only.
 */
export async function startAuthorization() {
  assertHostedLoginConfig();

  // Mark the session cookie-based so logout targets /oidc/session/revoke. This
  // survives the full-page redirect round trip (same-origin localStorage).
  localStorage.setItem('auth_method', 'cookie');

  const initiateUrl = new URL(`${OIDC_CONFIG.issuer}/idp/initiate`);
  initiateUrl.searchParams.set('x-blocks-key', OIDC_CONFIG.tenantId);
  // clientId / redirectUri are optional — send them only when configured, otherwise
  // let the platform fall back to the project's default OIDC client + redirect URI.
  if (OIDC_CONFIG.clientId) initiateUrl.searchParams.set('clientId', OIDC_CONFIG.clientId);
  if (OIDC_CONFIG.redirectUri) initiateUrl.searchParams.set('redirectUri', OIDC_CONFIG.redirectUri);

  const response = await fetch(initiateUrl.toString(), {
    method: 'GET',
    headers: { accept: '*/*', 'x-blocks-key': OIDC_CONFIG.tenantId },
    credentials: 'include',
  });

  // Shape A: JSON body carrying the hosted authorize URL. The canonical field is
  // `redirect_uri`; accept the documented legacy variants too.
  const data = await response.json().catch(() => null);
  const rawAuthUrl =
    data?.redirect_uri ||
    data?.redirectUri ||
    data?.authorizationUrl ||
    data?.authorization_url ||
    data?.url ||
    data?.redirectUrl;
  if (rawAuthUrl) {
    window.location.href = normalizeAuthUrl(rawAuthUrl);
    return;
  }

  // Shape B: the platform issued a 30x that fetch auto-followed — the final URL
  // is the hosted login page itself.
  if (response.redirected && response.url) {
    window.location.href = response.url;
    return;
  }

  console.warn('IDP initiate response had no redirect URL:', data);
  throw new Error('OIDC initiate response did not contain a redirect URL');
}

/**
 * Hosted-login return handler for the /callback page. In the cookie flow the
 * platform has already exchanged the code and set the HTTP-only session cookie
 * before redirecting here, so there is nothing to exchange — we only surface an
 * OAuth error if one came back in the query string. The session itself is then
 * resolved by AuthContext via `GET /auth/me` (cookie sent with credentials).
 */
export async function handleAuthorizationCallback() {
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  const errorDescription = params.get('error_description');
  if (error) throw new Error(errorDescription || error);
}

/**
 * Embedded (password) login — POST /auth/login. Body fields are **snake_case**.
 * Returns a discriminated result the login form branches on:
 *   { status: 'success', tokens }         — tokens persisted, session ready
 *   { status: 'mfa_required', mfaId, mfaType } — call again with mfaCode
 *   { status: 'captcha_required', captcha }     — call again with captchaCode
 * Throws Error(message) on invalid credentials / locked account / network error.
 *
 * The /auth/login response shape is undocumented in swagger — this reads several
 * likely field spellings defensively. Inspect a live response and tighten if needed.
 */
export async function loginWithPassword({
  username,
  password,
  captchaCode = '',
  mfaId = '',
  mfaCode = '',
  mfaType,
} = {}) {
  assertLoginConfig();

  const hasMfaType = mfaType !== undefined && mfaType !== null && mfaType !== '';
  const body = {
    username,
    password,
    // client_id is an optional /auth/login body field (snake_case, per swagger).
    // Sourced from the env (VITE_OIDC_CLIENT_ID / VITE_CLIENT_ID); sent when set.
    ...(OIDC_CONFIG.clientId ? { client_id: OIDC_CONFIG.clientId } : {}),
    ...(captchaCode ? { captcha_code: captchaCode } : {}),
    ...(mfaId ? { mfa_id: mfaId } : {}),
    ...(mfaCode ? { mfa_code: mfaCode } : {}),
    ...(hasMfaType ? { mfa_type: Number(mfaType) } : {}),
  };

  const response = await fetch(`${OIDC_CONFIG.issuer}/auth/login`, {
    method: 'POST',
    // Send/store cookies: this tenant issues the tokens as HTTP-only Set-Cookies
    // rather than in the JSON body, so the cookie must be accepted here.
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-blocks-key': OIDC_CONFIG.tenantId,
    },
    body: JSON.stringify(body),
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(extractAuthError(payload) || `Login failed (${response.status})`);
  }

  const tokens = normalizeTokenResponse(payload);
  if (tokens.access_token) {
    persistTokens(tokens, 'embedded');
    return { status: 'success', tokens };
  }

  // No tokens issued — the server is asking for a second factor or a captcha,
  // or returned a 200 error envelope. Detect each branch defensively.
  const p = payload?.data && typeof payload.data === 'object' ? payload.data : payload || {};

  const resolvedMfaId = p.mfa_id || p.mfaId || p.MfaId || p.challengeId || p.challenge_id;
  const mfaFlagged = p.requiresMfa || p.mfa_required || p.mfaRequired || p.isMfaRequired;
  if (resolvedMfaId || mfaFlagged) {
    return {
      status: 'mfa_required',
      mfaId: resolvedMfaId || '',
      mfaType: p.mfa_type ?? p.mfaType ?? p.MfaType,
    };
  }

  const captchaFlagged =
    p.requiresCaptcha || p.captcha_required || p.captchaRequired || p.isCaptchaRequired;
  const errText = (extractAuthError(payload) || '').toLowerCase();
  if (captchaFlagged || errText.includes('captcha')) {
    return {
      status: 'captcha_required',
      // If the server returns a captcha image/id inline, surface it for the form.
      captcha: p.captcha || p.captchaImage || p.captcha_image || p.captchaId || null,
    };
  }

  // No body tokens and no challenge, but a 2xx carrying token metadata
  // (token_type / expires_in) means the tenant issued the tokens as HTTP-only
  // cookies. Treat it as a cookie session: mark it so logout targets the session
  // endpoint, and let AuthContext resolve the user via GET /auth/me (the browser
  // sends the cookie with credentials: 'include').
  if (payload && (payload.token_type || payload.tokenType || payload.expires_in || payload.expiresIn)) {
    // Token-based tenant: /auth/login delivered the tokens as cookies rather than
    // in the JSON body. Read them so the bearer is attached to API calls and
    // refreshAccessToken() can renew via /oidc/token (per the refresh-token skill).
    // The refresh token lives in the rt_<host> cookie.
    const cookieAccess = readTokenFromCookies();
    const cookieRefresh = readRefreshTokenFromCookies();
    if (cookieAccess) {
      persistTokens({ access_token: cookieAccess, refresh_token: cookieRefresh }, 'oidc');
      return { status: 'success', tokens: { access_token: cookieAccess, refresh_token: cookieRefresh } };
    }
    // Tokens are HTTP-only / cross-domain and not readable by JS → fall back to a
    // server-resolved cookie session (GET /auth/me sends the cookie). No JS refresh.
    ['access_token', 'accessToken', 'authToken', 'token', 'blocks-os-token', 'id_token', 'refresh_token', 'token_expiry']
      .forEach((key) => localStorage.removeItem(key));
    localStorage.setItem('auth_method', 'cookie');
    return { status: 'success', cookie: true };
  }

  throw new Error(extractAuthError(payload) || 'Invalid username or password');
}

/**
 * Forgot-password step 1 — POST /auth/recover. Unauthenticated; body is camelCase.
 * Emails a recovery link (to the recoverAccountPath configured in /auth/config).
 * Resolves on 200; throws on network/4xx. To avoid leaking account existence, the
 * UI should show the same "check your email" message whether or not the address
 * exists — so treat a resolved promise as the only success signal.
 */
export async function recoverPassword({ email, captchaCode = '' } = {}) {
  assertLoginConfig();

  const body = {
    email,
    ...(captchaCode ? { captchaCode } : {}),
  };

  const response = await fetch(`${OIDC_CONFIG.issuer}/auth/recover`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-blocks-key': OIDC_CONFIG.tenantId,
    },
    body: JSON.stringify(body),
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(extractAuthError(payload) || `Could not send recovery email (${response.status})`);
  }
  return payload;
}

/**
 * Forgot-password step 2 — POST /auth/reset-password. Body is camelCase. `code`
 * comes from the recovery email link. Defaults `logoutFromAllDevices: true`
 * (recommended for a compromised-password reset). New password must satisfy the
 * tenant's passwordStrengthCheckerRegex. Throws on invalid/expired code.
 */
export async function resetPassword({
  code,
  password,
  captchaCode = '',
  logoutFromAllDevices = true,
} = {}) {
  assertLoginConfig();

  const body = {
    code,
    password,
    logoutFromAllDevices,
    ...(captchaCode ? { captchaCode } : {}),
  };

  const response = await fetch(`${OIDC_CONFIG.issuer}/auth/reset-password`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-blocks-key': OIDC_CONFIG.tenantId,
    },
    body: JSON.stringify(body),
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(
      extractAuthError(payload) || 'This reset link is invalid or has expired. Request a new one.',
    );
  }
  // Some tenants return { isSuccess: false, errors } with a 200 — treat that as failure.
  if (payload && payload.isSuccess === false) {
    throw new Error(extractAuthError(payload) || 'Password reset failed. Request a new link.');
  }
  return payload;
}

/**
 * Self-service signup — POST /auth/signup. Unauthenticated; body is camelCase.
 * Note: signup takes NO password. On success the platform emails an activation
 * link; the user sets their password on the activation page (see Activate.jsx →
 * POST /auth/activate). Response undocumented — inspect live. Throws on 4xx (e.g.
 * email already registered).
 */
export async function signup({
  email = '',
  firstName = '',
  lastName = '',
  phoneNumber = '',
  captchaCode = '',
} = {}) {
  assertLoginConfig();

  const body = {
    email,
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    ...(phoneNumber ? { phoneNumber } : {}),
    ...(captchaCode ? { captchaCode } : {}),
  };

  const response = await fetch(`${OIDC_CONFIG.issuer}/auth/signup`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-blocks-key': OIDC_CONFIG.tenantId,
    },
    body: JSON.stringify(body),
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(extractAuthError(payload) || `Sign up failed (${response.status})`);
  }
  // Some tenants return { isSuccess: false, errors } with a 200 — treat that as failure.
  if (payload && payload.isSuccess === false) {
    throw new Error(extractAuthError(payload) || 'Sign up failed. Please try again.');
  }
  return payload;
}

let refreshInFlight = null;

/**
 * Exchange the stored refresh_token for a fresh access/refresh pair and persist them.
 * Concurrent callers share a single in-flight request. Returns the new access token,
 * or '' if there is no refresh token or the refresh fails (caller should then re-login).
 */
/**
 * Whether a token refresh is even possible: either a JS-readable refresh token
 * (localStorage / `rt_<host>` cookie) OR a hosted cookie session, where the refresh
 * token lives in an httpOnly `rt_` cookie the browser sends automatically — so JS
 * can't see it, but the server can still refresh from it via credentials: 'include'.
 */
export function canRefresh() {
  // Possible if we have a JS-readable refresh token OR any known session: the refresh
  // token is often an httpOnly rt_ cookie that JS can't see but the server reads on a
  // credentialed /auth/refresh — so treat any auth_method (embedded/oidc/cookie) as
  // refreshable.
  return hasRefreshToken() || Boolean(localStorage.getItem('auth_method'));
}

/**
 * Refresh the access token / session and persist it. Concurrent callers share one
 * in-flight request. Returns a truthy value on success ('' on failure — caller
 * should re-login). Handles three shapes:
 *  - cookie session (httpOnly rt_ cookie): POST /auth/refresh with credentials; the
 *    server reads the cookie and Set-Cookies a fresh pair (no readable body token).
 *  - embedded (readable refresh_token): POST /auth/refresh with the token in the body.
 *  - legacy OIDC: /oidc/token with client-credential Basic auth.
 */
export async function refreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = getRefreshToken();
  const authMethod = localStorage.getItem('auth_method');
  // Refresh is worth attempting when we have a readable token OR any known session
  // (server may hold an httpOnly rt_ cookie). Bail only when there's no session at all.
  if (!refreshToken && !authMethod) return '';

  refreshInFlight = (async () => {
    try {
      let response;

      if (authMethod === 'oidc' && refreshToken) {
        // Legacy OIDC sessions refresh via the token endpoint with Basic client auth.
        const tokenUrl = `${OIDC_CONFIG.issuer}/oidc/token?tenant_id=${encodeURIComponent(OIDC_CONFIG.tenantId)}`;
        const basicAuth = btoa(`${OIDC_CONFIG.clientId}:${OIDC_CONFIG.clientSecret}`);
        response = await fetch(tokenUrl, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'application/json',
            Authorization: `Basic ${basicAuth}`,
            'x-blocks-key': OIDC_CONFIG.tenantId,
          },
          body: new URLSearchParams({
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
            client_id: OIDC_CONFIG.clientId,
          }),
        });
      } else {
        // Embedded + cookie sessions: POST /auth/refresh with credentials so the server
        // can read the httpOnly rt_ cookie when JS has no readable token. Include the
        // refresh_token in the body when we do have one.
        response = await fetch(`${OIDC_CONFIG.issuer}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'x-blocks-key': OIDC_CONFIG.tenantId,
          },
          body: JSON.stringify(refreshToken ? { refresh_token: refreshToken } : {}),
        });
      }

      if (!response.ok) {
        // Refresh token expired/invalid — clear the dead session so the app re-gates to login.
        clearAuthStorage();
        return '';
      }

      const tokens = normalizeTokenResponse(await response.json().catch(() => ({})));
      if (tokens.access_token) {
        // Refresh tokens ROTATE on every use — persistTokens writes the new one.
        persistTokens(tokens, authMethod || undefined);
        return tokens.access_token;
      }
      // Refresh returned 2xx but no readable body token — the new tokens were set as
      // httpOnly cookies. The browser now holds a fresh session; signal success so the
      // caller retries the original request (works for embedded/oidc/cookie sessions
      // whose refresh token is an httpOnly rt_ cookie).
      return 'cookie';
    } catch {
      return '';
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Log out — POST /auth/logout (the documented app-auth endpoint; NOT the PascalCase
 * platform /api/auth/Logout back-office route). Per swagger it "revokes the refresh
 * token, invalidates the session, and clears cookies", so it covers both cookie and
 * token sessions in one call. Body field is camelCase `refreshToken` (optional).
 * `credentials: 'include'` sends the session cookies so the server can clear them.
 */
export async function logout() {
  const refreshToken = localStorage.getItem('refresh_token') || readRefreshTokenFromCookies();

  clearAuthStorage();

  try {
    await fetch(`${OIDC_CONFIG.issuer}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-blocks-key': OIDC_CONFIG.tenantId,
      },
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    });
  } catch {
    // Best-effort — local storage is already cleared, so the app re-gates to login.
  }

  clearAuthStorage();
}

/** Returns true when a non-expired access token exists in localStorage. */
export function isAuthenticated() {
  const expiry = localStorage.getItem('token_expiry');
  return Boolean(expiry && Date.now() < Number.parseInt(expiry, 10));
}

/** Parse `document.cookie` into a { name: value } map. */
function parseCookies() {
  if (typeof document === 'undefined' || !document.cookie) return {};
  return Object.fromEntries(
    document.cookie.split(';').map((c) => {
      const i = c.indexOf('=');
      return i === -1
        ? [c.trim(), '']
        : [c.slice(0, i).trim(), decodeURIComponent(c.slice(i + 1).trim())];
    }),
  );
}

/** True when a string looks like a JWT (three dot-separated segments). */
function looksLikeJwt(v) {
  return typeof v === 'string' && v.split('.').length === 3;
}

/**
 * Read the Blocks IAM access token from cookies. Blocks construct apps store the
 * access token in a cookie named after the app host (e.g. `app.example.com=<jwt>`),
 * so a page load with only cookies still has a usable token.
 */
function readTokenFromCookies() {
  const cookies = parseCookies();
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const candidates = [host, 'access_token', 'accessToken', 'authToken', 'token', 'blocks-os-token'];
  for (const name of candidates) {
    if (looksLikeJwt(cookies[name])) return cookies[name];
  }
  // Fall back to any cookie whose value looks like a JWT (skip the `rt_` refresh cookie).
  for (const [name, value] of Object.entries(cookies)) {
    if (!name.startsWith('rt_') && looksLikeJwt(value)) return value;
  }
  return '';
}

/** Read the refresh token from cookies (`rt_<host>`), used when localStorage has none. */
function readRefreshTokenFromCookies() {
  const cookies = parseCookies();
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  return cookies[`rt_${host}`] || cookies.rt_token || cookies.refresh_token || '';
}

export function getRefreshToken() {
  return localStorage.getItem('refresh_token') || readRefreshTokenFromCookies() || '';
}

export function hasRefreshToken() {
  return Boolean(getRefreshToken());
}

/**
 * Ensure a usable access token before an API call. Refreshes (via the refresh token
 * in localStorage or the `rt_<host>` cookie) when there is NO access token at all —
 * the common case where only the long-lived refresh cookie survived — or when the
 * current access token is within a 30s skew of expiring.
 */
export async function ensureFreshToken() {
  const hasAccessToken = Boolean(
    localStorage.getItem('access_token') || import.meta.env.VITE_AUTH_TOKEN || readTokenFromCookies(),
  );
  const expiry = Number.parseInt(localStorage.getItem('token_expiry') || '0', 10);
  const nearExpiry = expiry && Date.now() > expiry - 30_000;

  if ((!hasAccessToken || nearExpiry) && hasRefreshToken()) await refreshAccessToken();
}

/**
 * Returns Authorization header for authenticated API calls.
 * Reads the token from env → localStorage → cookies. Empty object if none.
 */
export function getAuthHeaders() {
  const token =
    import.meta.env.VITE_AUTH_TOKEN ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('accessToken') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    localStorage.getItem('blocks-os-token') ||
    readTokenFromCookies();

  if (!token) return {};

  const trimmedToken = token.trim();
  if (!trimmedToken || trimmedToken === 'undefined' || trimmedToken === 'null') return {};

  return {
    Authorization: /^Bearer\s+/i.test(trimmedToken) ? trimmedToken : `Bearer ${trimmedToken}`,
  };
}

// ── Authenticated IAM calls (profile, change password) ──────────────────────────

/**
 * Authenticated fetch against the IAM service. Mirrors the data gateway pattern:
 * ensure a fresh token, send x-blocks-key + Bearer, and on a 401 refresh once and
 * retry. Returns the parsed JSON body. Throws Error (with `.status`) on failure,
 * including a 200 `{ isSuccess: false }` envelope.
 */
async function iamFetch(path, options = {}) {
  const method = options.method || 'GET';
  const body = options.body;

  const headers = {
    Accept: 'application/json',
    'x-blocks-key': OIDC_CONFIG.tenantId,
    ...getAuthHeaders(),
  };
  if (body) headers['Content-Type'] = 'application/json';

  const init = { method, credentials: 'include', headers };
  if (body) init.body = JSON.stringify(body);

  const doFetch = () => fetch(`${OIDC_CONFIG.issuer}${path}`, init);

  await ensureFreshToken();
  let response = await doFetch();

  if (response.status === 401 && canRefresh()) {
    const refreshed = await refreshAccessToken();
    if (refreshed) response = await doFetch();
  }

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const err = new Error(extractAuthError(payload) || `iam ${method} ${path} → ${response.status}`);
    err.status = response.status;
    throw err;
  }
  if (payload && payload.isSuccess === false) {
    throw new Error(extractAuthError(payload) || `iam ${method} ${path} failed`);
  }
  return payload;
}

/**
 * GET /auth/me — OIDC UserInfo claims (sub, email, name, picture, custom claims)
 * for the current access token. Shape undocumented — returns the raw object.
 */
export async function getMe() {
  return iamFetch('/auth/me');
}

/**
 * GET /iam/me — the IAM profile document, enveloped as `{ data, errors }` where
 * `data` is an open key/value map (firstName, lastName, phoneNumber, …).
 * Returns just the `data` object (or {} if absent).
 */
export async function getProfile() {
  const payload = await iamFetch('/iam/me');
  return payload?.data ?? {};
}

/**
 * PATCH /iam/me — self-service profile edit. Body is camelCase; pass only the
 * fields you want to change (firstName, lastName, phoneNumber, profileImageUrl,
 * salutation, …). Returns the raw response.
 */
export async function updateProfile(patch) {
  return iamFetch('/iam/me', { method: 'PATCH', body: patch });
}

/**
 * POST /auth/change-password — authenticated password change (camelCase body).
 * The current password is re-validated server-side.
 *
 * Respects the tenant's `logoutOnPasswordChange`: after a successful change we
 * probe the session with GET /auth/me. If that comes back unauthorized (the
 * server revoked the session), we report `sessionInvalidated: true` so the caller
 * can send the user back through login. Returns { sessionInvalidated: boolean }.
 */
export async function changePassword({ oldPassword, newPassword }) {
  await iamFetch('/auth/change-password', {
    method: 'POST',
    body: { oldPassword, newPassword },
  });

  try {
    await getMe();
    return { sessionInvalidated: false };
  } catch (err) {
    if (err?.status === 401) return { sessionInvalidated: true };
    // A non-auth error on the probe doesn't mean the change failed — assume the
    // session is still valid and let the caller proceed.
    return { sessionInvalidated: false };
  }
}
